/**
 * The lift panel's Robot use section (Akash, 2026-11-02, decisions A, B, C, E).
 *
 * One or two switches, each with its state, why it is that way, when it next
 * changes and who a change is waiting on. SUPERVISORS ONLY press anything: the
 * front desk reads the same section with no buttons.
 *
 * THE SWITCH IS A PICTURE OF WHAT THE ROBOTS' SYSTEM REPORTS, not a control, and
 * is drawn only while something is reporting. A press opens a question (how long,
 * who else's block is this, are you sure); nothing changes on one tap.
 */
import { useContext, useId, useMemo, useState, type ReactNode } from 'react';
import { useInRouterContext, useNavigate } from 'react-router-dom';
import type { LiftViewModel } from '../../api/adapters';
import type {
  LiftRuleLiftView,
  LiftRuleScope,
  LiftRuleScopeView,
  LiftRuleWindow,
  LiftRulesDelivery,
} from '../../api/v1';
import { useTasks } from '../../api/hooks';
import { apiErrorCode } from '../../api/hooks/useFireMode';
import {
  cutOffLevelsOf,
  LIFT_RULES_NOT_REPORTING,
  liftRulesOn,
  useLiftRuleOccurrences,
  useLiftRules,
  useLiftRuleWindows,
  useReleaseLiftRuleHold,
  useResetLiftRuleBreaker,
  useSetLiftRuleHold,
} from '../../api/hooks/useLiftRules';
import { AuthContext } from '../../auth/AuthContext';
import { hasAnyRole } from '../../auth/roles';
import { LIFT_RULE_SWITCH_ROLES } from '../../auth/types';
import { useActionsOff } from '../../auth/useRolePreview';
import { ConfirmDialog } from '../../design-system/components/ConfirmDialog';
import { StatusChip } from '../../design-system/components/StatusChip';
import { HelpNote } from '../../help';
import { useMinuteTick } from '../../state/clock';
import { closedLiftScopes, hasNoRoute, type ClosedLiftScope } from '../../state/lift-rule-waiting';
import { useActiveSite, useSafeSiteId } from '../../state/useActiveSite';
import { dateKeyOf, addDays, blockRange } from '../Tasks/calendar/lane-time';
import { describeRule } from '../Tasks/rrule';
import { resolveSiteTimeZone } from '../Tasks/site-timezone';
import { formatZonedClock, formatZonedDateTime } from '../Tasks/zoned-time';
import { durationWords } from './devices-rows';
import { formatClock, robotName } from './lift-reading';
import {
  cutOffSentence,
  holdSetBy,
  nextChangeWords,
  NOT_COMMISSIONED,
  readScope,
  ROBOT_USE,
  scopeSourceWords,
  showsTravelSwitch,
  TRAVEL_BETWEEN_FLOORS,
  WINDOW_KIND_WORDS,
  type LiftRuleClock,
  type LiftRuleWho,
} from './lift-rules-words';
import './LiftRobotUseSection.css';

const HOUR_MS = 3_600_000;

/** Where a press's refusal goes, in words. */
function refusalWords(err: unknown): string {
  const code = apiErrorCode(err);
  if (code === LIFT_RULES_NOT_REPORTING) {
    return 'The robots’ system is not reporting its lifts just now, so nothing was changed. Try again shortly.';
  }
  if (code === 'lift_rules_off') return 'Lift rules are switched off for this site, so nothing was changed.';
  if (code === 'lift_not_commissioned') return 'This lift is not set up for robots, so nothing was changed.';
  if (code === 'lift_rule_unavailable') {
    return 'Travel between floors is built out of this site’s map, so there is nothing to change.';
  }
  return err instanceof Error && err.message ? err.message : 'That did not work. Nothing was changed.';
}

export function LiftRobotUseSection({
  lift,
  focus = false,
}: {
  lift: Pick<LiftViewModel, 'name' | 'levels'>;
  /** Opened by the table's Change…: brought into view. */
  focus?: boolean;
}) {
  const answer = useLiftRules();
  const auth = useContext(AuthContext);
  const actionsOff = useActionsOff();
  const { sites } = useActiveSite();
  const siteId = useSafeSiteId();
  const zone = resolveSiteTimeZone(sites, siteId);
  const nowMs = useMinuteTick();
  const siteZone = zone.source === 'site' ? zone.timeZone : null;
  const clock: LiftRuleClock = (ms) => formatClock(ms, siteZone, nowMs);
  const who: LiftRuleWho = { mySub: auth?.user?.id ?? null, myEmail: auth?.user?.email ?? null };
  const canSwitch = hasAnyRole(auth?.roles, LIFT_RULE_SWITCH_ROLES) && !actionsOff;
  const focusRef = (el: HTMLElement | null) => {
    if (focus && el) el.scrollIntoView?.({ block: 'start' });
  };

  if (!liftRulesOn(answer.state)) return null;
  if (answer.state === 'not_reporting' || answer.state === 'error') {
    return (
      <section
        className="nx-lift-rules"
        aria-label={ROBOT_USE}
        ref={focusRef}
        data-testid="lift-rules-not-reporting"
      >
        <h3 className="nx-lift-rules__h">{ROBOT_USE}</h3>
        <div className="nx-lift-rules__notice">
          <StatusChip status="offline" label="Not known" dashed />
          <p>
            {answer.state === 'not_reporting'
              ? 'The robots’ system is not reporting its lifts just now, so what robots may do here is not known and nothing can be changed. Whatever was set still stands. If this lasts, call Nexsoss.'
              : 'The rules for this lift could not be read. That says nothing about the lift.'}
          </p>
        </div>
      </section>
    );
  }

  const view = answer.view;
  // `liftRulesOn`, and neither failure above: a site that is on and reporting.
  if (!view) return null;
  const rule = view.lifts.find((l) => l.lift === lift.name) ?? null;
  // Robots have no route through a lift the fleet does not report.
  if (!rule) return null;
  const closed = closedLiftScopes(view);

  if (!rule.commissioned) {
    return (
      <section
        className="nx-lift-rules"
        aria-label={ROBOT_USE}
        ref={focusRef}
        data-testid="lift-rules-not-commissioned"
      >
        <h3 className="nx-lift-rules__h">{ROBOT_USE}</h3>
        <div className="nx-lift-rules__notice">
          <StatusChip status="offline" label={NOT_COMMISSIONED} dashed />
          <p>This lift is not set up for robots. Nexsoss sets it up, so there is nothing to change here.</p>
        </div>
      </section>
    );
  }

  const delivery = view.delivery;
  // A rule that has stopped reaching the robots: nobody is listening, so nothing
  // is offered. The alert says the same (`lift_rule_not_delivered`).
  const writable = canSwitch && delivery.state !== 'failed';
  return (
    <section className="nx-lift-rules" aria-label={ROBOT_USE} ref={focusRef} data-testid="lift-rules">
      <h3 className="nx-lift-rules__h">{ROBOT_USE}</h3>
      {delivery.state === 'failed' && <NotDelivered delivery={delivery} clock={clock} />}
      <WaitingRobots lift={lift.name} closed={closed} />
      <ScopeCard
        rule={rule}
        scope="use"
        served={lift.levels}
        clock={clock}
        who={who}
        canSwitch={writable}
        canReset={canSwitch}
        timeZone={zone.timeZone}
        nowMs={nowMs}
      />
      {showsTravelSwitch(rule) && (
        <ScopeCard
          rule={rule}
          scope="between_floors"
          served={lift.levels}
          clock={clock}
          who={who}
          canSwitch={writable}
          canReset={canSwitch}
          timeZone={zone.timeZone}
          nowMs={nowMs}
        />
      )}
      <CalendarWindows
        lift={lift.name}
        canAdd={writable}
        fireActive={view.fire_active}
      />
      <HelpNote id="devices.lift.rules" />
    </section>
  );
}

function NotDelivered({ delivery, clock }: { delivery: LiftRulesDelivery; clock: LiftRuleClock }) {
  const since = typeof delivery.failed_since_ms === 'number' ? ` since ${clock(delivery.failed_since_ms)}` : '';
  return (
    <p className="nx-lift-rules__alert" role="status" data-testid="lift-rules-not-delivered">
      Rules have not reached the robots{since}. Until they do, robots may still use a lift you
      blocked, and nothing can be changed here. Call Nexsoss.
    </p>
  );
}

/**
 * Who is waiting on a blocked lift. RMF names the destination, not the lift, so
 * this names only what it can: with one scope closed at the site it is this one,
 * and with several it may be this one. A lift with nothing closed says nothing,
 * since RMF's line alone has other causes.
 */
function WaitingRobots({
  lift,
  closed,
}: {
  lift: string;
  closed: readonly ClosedLiftScope[] | null;
}) {
  const tasks = useTasks().data;
  const nowMs = useMinuteTick();
  const here = (closed ?? []).some((c) => c.lift === lift);
  const waiting = useMemo(
    () => (here ? (tasks ?? []).filter((t) => hasNoRoute(t) && t.assignedRobot) : []),
    [here, tasks],
  );
  if (waiting.length === 0) return null;
  const only = closed?.length === 1;
  return (
    <ul className="nx-lift-rules__waiting" data-testid="lift-rules-waiting">
      {waiting.map((t) => (
        <li key={t.id}>
          <strong>{robotName(t.assignedRobot!)}</strong>{' '}
          {only ? 'is waiting for this lift' : 'is waiting while a lift is blocked'}
          {typeof t.noRouteSinceMs === 'number'
            ? ` · ${durationWords(Math.max(0, nowMs - t.noRouteSinceMs))}`
            : ''}
          {only
            ? '. Its task carries on when this is released.'
            : '. More than one lift is blocked, so it may not be this one.'}
        </li>
      ))}
    </ul>
  );
}

function SwitchGlyph({ on }: { on: boolean | null }) {
  if (on === null) return null;
  return <span className={`nx-lift-rules__switch${on ? ' is-on' : ''}`} aria-hidden="true" />;
}

function Facts({ rows }: { rows: ReadonlyArray<readonly [string, ReactNode]> }) {
  return (
    <dl className="nx-lift-rules__facts">
      {rows.map(([k, v]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function ScopeCard({
  rule,
  scope,
  served,
  clock,
  who,
  canSwitch,
  canReset,
  timeZone,
  nowMs,
}: {
  rule: LiftRuleLiftView;
  scope: LiftRuleScope;
  served: readonly string[];
  clock: LiftRuleClock;
  who: LiftRuleWho;
  canSwitch: boolean;
  canReset: boolean;
  timeZone: string;
  nowMs: number;
}) {
  const view: LiftRuleScopeView = rule[scope];
  const reading = readScope(rule, view);
  const title = scope === 'use' ? ROBOT_USE : TRAVEL_BETWEEN_FLOORS;
  const next = nextChangeWords(view, clock);
  const [asking, setAsking] = useState(false);
  const noSwitch = reading.state === 'no_lanes' || reading.state === 'built_same_floor';
  const hold = view.hold ?? null;
  return (
    <div className="nx-lift-rules__scope" data-testid={`lift-rule-${scope}`}>
      <div className="nx-lift-rules__scope-top">
        <span className="nx-lift-rules__scope-title">{title}</span>
        <SwitchGlyph on={reading.switchOn} />
      </div>
      <div className="nx-lift-rules__scope-state">
        <span data-testid={`lift-rule-chip-${scope}`}>
          <StatusChip status={reading.status} label={reading.label} dashed={reading.dashed} />
        </span>
        {!noSwitch && reading.state !== 'latched' && (
          <span className="nx-lift-rules__source" data-testid={`lift-rule-source-${scope}`}>
            {scopeSourceWords(view, clock, who)}
          </span>
        )}
      </div>
      {reading.note && (
        <p className="nx-lift-rules__note" data-testid={`lift-rule-note-${scope}`}>
          {reading.note}
        </p>
      )}
      {reading.state === 'latched' && (
        <BreakerExplanation lift={rule.lift} scope={scope} canReset={canReset} />
      )}
      {!noSwitch && next && <Facts rows={[['Next change', next]]} />}
      {!noSwitch && canSwitch && reading.state !== 'latched' && (
        <ScopeActions
          lift={rule.lift}
          scope={scope}
          view={view}
          who={who}
          asking={asking}
          setAsking={setAsking}
          served={served}
          timeZone={timeZone}
          nowMs={nowMs}
          hold={hold !== null}
        />
      )}
    </div>
  );
}

function BreakerExplanation({
  lift,
  scope,
  canReset,
}: {
  lift: string;
  scope: LiftRuleScope;
  canReset: boolean;
}) {
  const reset = useResetLiftRuleBreaker();
  const [confirming, setConfirming] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  return (
    <div className="nx-lift-rules__notice" data-testid={`lift-rule-breaker-${scope}`}>
      <p>
        This rule changed too many times in a short while, so the lift was kept closed for safety.
        It stays closed until a supervisor resets it.
      </p>
      {canReset && (
        <div className="nx-lift-rules__buttons">
          <button
            type="button"
            className="nx-screen-btn"
            disabled={reset.isPending}
            onClick={() => {
              setRefusal(null);
              setConfirming(true);
            }}
            data-testid={`lift-rule-breaker-reset-${scope}`}
          >
            Reset…
          </button>
        </div>
      )}
      {refusal && (
        <p className="nx-lift-rules__error" role="alert">
          {refusal}
        </p>
      )}
      <ConfirmDialog
        open={confirming}
        title={`Reset ${lift}?`}
        message="The lift will then follow its rules again, which may reopen it to robots."
        confirmLabel="Reset"
        variant="warning"
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          reset.mutate(
            { lift, scope },
            {
              onError: (err) =>
                setRefusal(
                  (err as { status?: number }).status === 409
                    ? 'It has already been reset.'
                    : refusalWords(err),
                ),
            },
          );
        }}
      />
    </div>
  );
}

function ScopeActions({
  lift,
  scope,
  view,
  who,
  asking,
  setAsking,
  served,
  timeZone,
  nowMs,
  hold,
}: {
  lift: string;
  scope: LiftRuleScope;
  view: LiftRuleScopeView;
  who: LiftRuleWho;
  asking: boolean;
  setAsking: (v: boolean) => void;
  served: readonly string[];
  timeZone: string;
  nowMs: number;
  hold: boolean;
}) {
  const release = useReleaseLiftRuleHold();
  const [confirmRelease, setConfirmRelease] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  // Allow travel now is a release, and only inside a run the calendar allows:
  // the view says nothing about the run a block is covering (`decide`).
  const range = useMemo(
    () =>
      scope === 'between_floors'
        ? { fromMs: Math.floor(nowMs / HOUR_MS) * HOUR_MS, toMs: nowMs + 60_000 }
        : null,
    [scope, nowMs],
  );
  const occurrences = useLiftRuleOccurrences(range).data?.occurrences;
  const insideAllowed = (occurrences ?? []).some(
    (o) =>
      o.lift === lift && o.kind === 'allow_travel' && o.starts_at_ms <= nowMs && nowMs < o.ends_at_ms,
  );
  const send = () => {
    setRefusal(null);
    release.mutate({ lift, scope }, { onError: (err) => setRefusal(refusalWords(err)) });
  };
  const byWhom = hold ? holdSetBy(view, who) : '';
  const pressRelease = () => {
    if (byWhom === 'you') send();
    else setConfirmRelease(true);
  };
  const isUse = scope === 'use';
  const blockLabel = isUse ? 'Block from robot use…' : 'Block travel between floors…';
  const sameFloorByDefault = !isUse && !hold && view.desired === 'closed';
  const releaseLabel = !isUse && insideAllowed ? 'Allow travel now' : 'Release block';

  if (asking) {
    return (
      <BlockForm
        lift={lift}
        scope={scope}
        served={served}
        timeZone={timeZone}
        nowMs={nowMs}
        changing={hold}
        onDone={() => setAsking(false)}
      />
    );
  }
  return (
    <>
      <div className="nx-lift-rules__buttons">
        {hold ? (
          <>
            <button
              type="button"
              className="nx-screen-btn"
              disabled={release.isPending}
              onClick={pressRelease}
              data-testid={`lift-rule-release-${scope}`}
            >
              {releaseLabel}
            </button>
            <button
              type="button"
              className="nx-screen-btn"
              onClick={() => setAsking(true)}
              data-testid={`lift-rule-change-${scope}`}
            >
              Change how long…
            </button>
          </>
        ) : sameFloorByDefault ? (
          <>
            <button
              type="button"
              className="nx-screen-btn"
              disabled
              data-testid={`lift-rule-allow-${scope}`}
            >
              Allow travel now
            </button>
            <span className="nx-lift-rules__why">Only inside a time the calendar allows</span>
          </>
        ) : (
          <button
            type="button"
            className="nx-screen-btn"
            onClick={() => setAsking(true)}
            data-testid={`lift-rule-block-${scope}`}
          >
            {blockLabel}
          </button>
        )}
      </div>
      {refusal && (
        <p className="nx-lift-rules__error" role="alert">
          {refusal}
        </p>
      )}
      <ConfirmDialog
        open={confirmRelease}
        title={`Release the block on ${lift}?`}
        message={`${byWhom === 'a team member' ? 'A team member' : byWhom} set this block. Robots may use this again once it is released.`}
        confirmLabel="Release it"
        variant="warning"
        onCancel={() => setConfirmRelease(false)}
        onConfirm={() => {
          setConfirmRelease(false);
          send();
        }}
      />
    </>
  );
}

type UntilChoice = 'hour' | 'time' | 'released' | 'next_change';

/** The next time this clock reads `hhmm` on the site's clock. */
function nextAt(hhmm: string, nowMs: number, timeZone: string): number | null {
  const today = dateKeyOf(nowMs, timeZone);
  // `blockRange` reads a wall clock on the site's zone; the start is unused.
  const range = blockRange(today, '00:00', hhmm, timeZone);
  if (!range) return null;
  if (range.endMs > nowMs) return range.endMs;
  return blockRange(addDays(today, 1), '00:00', hhmm, timeZone)?.endMs ?? null;
}

function BlockForm({
  lift,
  scope,
  served,
  timeZone,
  nowMs,
  changing,
  onDone,
}: {
  lift: string;
  scope: LiftRuleScope;
  served: readonly string[];
  timeZone: string;
  nowMs: number;
  changing: boolean;
  onDone: () => void;
}) {
  const id = useId();
  const isUse = scope === 'use';
  // Decision B: the default differs by scope.
  const [choice, setChoice] = useState<UntilChoice>(isUse ? 'released' : 'next_change');
  const [time, setTime] = useState(() => formatZonedClock(nowMs + HOUR_MS, timeZone));
  const [reason, setReason] = useState('');
  const [cutOff, setCutOff] = useState<readonly string[] | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const put = useSetLiftRuleHold();
  const timeAt = nextAt(time, nowMs, timeZone);
  const until = (): 'released' | 'next_change' | number | null => {
    if (choice === 'released' || choice === 'next_change') return choice;
    if (choice === 'hour') return Date.now() + HOUR_MS;
    return timeAt;
  };
  const send = (confirm: boolean) => {
    const value = until();
    if (value === null) return;
    setRefusal(null);
    const trimmed = reason.trim();
    put.mutate(
      {
        lift,
        scope,
        body: {
          until: value,
          ...(trimmed ? { reason: trimmed } : {}),
          ...(confirm ? { confirm_cut_off: true } : {}),
        },
      },
      {
        onSuccess: onDone,
        onError: (err) => {
          const levels = cutOffLevelsOf(err);
          if (levels) setCutOff(levels);
          else {
            setCutOff(null);
            setRefusal(refusalWords(err));
          }
        },
      },
    );
  };
  const choices: ReadonlyArray<readonly [UntilChoice, string]> = isUse
    ? [
        ['hour', 'For 1 hour'],
        ['time', 'Until a time'],
        ['released', 'Until released'],
      ]
    : [
        ['hour', 'For 1 hour'],
        ['time', 'Until a time'],
        ['next_change', 'Until the next calendar change'],
        ['released', 'Until released'],
      ];
  if (cutOff) {
    return (
      <div className="nx-lift-rules__form" data-testid={`lift-rule-cutoff-${scope}`}>
        <p className="nx-lift-rules__cutoff" data-testid="lift-rule-cutoff-words">
          {cutOffSentence(cutOff, served)}
        </p>
        <div className="nx-lift-rules__buttons">
          <button
            type="button"
            className="nx-screen-btn nx-screen-btn--primary"
            disabled={put.isPending}
            onClick={() => send(true)}
            data-testid="lift-rule-cutoff-confirm"
          >
            Block it
          </button>
          <button type="button" className="nx-screen-btn" onClick={() => setCutOff(null)}>
            Go back
          </button>
        </div>
        {refusal && (
          <p className="nx-lift-rules__error" role="alert">
            {refusal}
          </p>
        )}
      </div>
    );
  }
  return (
    <form
      className="nx-lift-rules__form"
      data-testid={`lift-rule-form-${scope}`}
      onSubmit={(e) => {
        e.preventDefault();
        send(false);
      }}
    >
      <fieldset className="nx-lift-rules__choices">
        <legend>How long?</legend>
        {choices.map(([value, label]) => (
          <label key={value} className="nx-lift-rules__choice">
            <input
              type="radio"
              name={`${id}-until`}
              value={value}
              checked={choice === value}
              onChange={() => setChoice(value)}
              data-testid={`lift-rule-until-${value}`}
            />
            <span>{label}</span>
          </label>
        ))}
      </fieldset>
      {choice === 'time' && (
        <label className="nx-lift-rules__field">
          <span>Until</span>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            data-testid="lift-rule-until-clock"
          />
          {timeAt !== null && (
            <span className="nx-lift-rules__why" data-testid="lift-rule-until-readback">
              {formatZonedDateTime(timeAt, timeZone)}
            </span>
          )}
        </label>
      )}
      <label className="nx-lift-rules__field">
        <span>Reason (optional)</span>
        <input
          type="text"
          maxLength={200}
          value={reason}
          placeholder="e.g. Ballroom event"
          onChange={(e) => setReason(e.target.value)}
          data-testid="lift-rule-reason"
        />
      </label>
      <p className="nx-lift-rules__why">A robot already riding finishes its ride first.</p>
      <div className="nx-lift-rules__buttons">
        <button
          type="submit"
          className="nx-screen-btn nx-screen-btn--primary"
          disabled={put.isPending || until() === null}
          data-testid="lift-rule-form-send"
        >
          {changing ? 'Change it' : isUse ? 'Block from robot use' : 'Block travel'}
        </button>
        <button type="button" className="nx-screen-btn" onClick={onDone}>
          Cancel
        </button>
      </div>
      {refusal && (
        <p className="nx-lift-rules__error" role="alert">
          {refusal}
        </p>
      )}
    </form>
  );
}

/** "Mon–Fri 23:00–06:00"-style, from the rule's own words. */
function windowWhen(w: LiftRuleWindow): string {
  const end = w.starts_at_ms + w.duration_ms;
  if (!w.rrule) {
    return `${formatZonedDateTime(w.starts_at_ms, w.timezone)} → ${formatZonedClock(end, w.timezone)}`;
  }
  try {
    return `${describeRule(w.rrule, w.starts_at_ms, w.repeat_until_ms, w.timezone, { siteTimeZone: w.timezone })} → ${formatZonedClock(end, w.timezone)}`;
  } catch {
    return `From ${formatZonedDateTime(w.starts_at_ms, w.timezone)}`;
  }
}

function CalendarWindows({
  lift,
  canAdd,
  fireActive,
}: {
  lift: string;
  canAdd: boolean;
  fireActive: boolean;
}) {
  const windows = useLiftRuleWindows().data;
  const inRouter = useInRouterContext();
  const nowMs = useMinuteTick();
  const shown = (windows ?? [])
    .filter((w) => w.lift === lift && w.enabled)
    .filter((w) => {
      const lastEnd = w.rrule ? (w.repeat_until_ms ?? Infinity) : w.starts_at_ms + w.duration_ms;
      return lastEnd > nowMs;
    });
  return (
    <div className="nx-lift-rules__calendar" data-testid="lift-rules-calendar">
      <div className="nx-lift-rules__calendar-head">
        <h4>On the calendar</h4>
        {canAdd && inRouter && <AddToCalendar lift={lift} />}
      </div>
      {shown.length === 0 ? (
        <p className="nx-lift-rules__why">Nothing planned.</p>
      ) : (
        <ul>
          {shown.map((w) => (
            <li key={w.window_id} className={`is-${w.kind}`}>
              <strong>{WINDOW_KIND_WORDS[w.kind]}</strong>
              <span className="nx-lift-rules__why">
                {windowWhen(w)}
                {w.reason ? ` · “${w.reason}”` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
      {fireActive && (
        <p className="nx-lift-rules__why">
          While the fire alarm stands, nothing opens to robots, whatever the calendar says.
        </p>
      )}
    </div>
  );
}

function AddToCalendar({ lift }: { lift: string }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="nx-lift-rules__link"
      onClick={() => navigate('/tasks?view=calendar&cal=day', { state: { openBlockTime: { lift } } })}
      data-testid="lift-rule-add-calendar"
    >
      Add to calendar…
    </button>
  );
}
